import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaCarouselType } from 'embla-carousel';
import SvgIcon from '../../utils/SvgIcon';

type ImageCarouselProps = Readonly<{
  imageLoader: () => Promise<string[]>;
  customClass?: string;
  isSmall?: boolean;
}>;

export default function ImageCarousel({ imageLoader, customClass, isSmall = false }: ImageCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    active: false,
  });
  const [images, setImages] = useState<string[] | null>([]);

  useEffect(() => {
    imageLoader().then(setImages);
  }, [imageLoader]);

  useEffect(() => {
    setTimeout(() => emblaApi?.reInit({ active: true }), 1000);
  }, [images, emblaApi]);

  if (images.length === 0) {
    return <p>No images found.</p>;
  }

  return (
    <section className="embla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {images.map((image, index) => (
            <div className={`embla__slide embla__slide--full${isSmall ? ' embla__slide--small' : ''}`} key={image}>
              <img
                src={image}
                className={`portal-image-carousel__image ${customClass ?? ''}`}
                alt={`Item ${index + 1} of ${images.length}`}
              />
            </div>
          ))}
        </div>
      </div>
      {emblaApi && images.length > 1 ? <CarouselPagination emblaApi={emblaApi} /> : null}
    </section>
  );
}

function CarouselPagination({ emblaApi }: Readonly<{ emblaApi: EmblaCarouselType }>) {
  const [selectedSnap, setSelectedSnap] = useState(0);
  const [snapCount, setSnapCount] = useState(0);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);

  const updateScrollSnapState = useCallback((emblaApi: EmblaCarouselType) => {
    setSnapCount(emblaApi.scrollSnapList().length);
    setSelectedSnap(emblaApi.selectedScrollSnap());
    setCanScrollNext(emblaApi.canScrollNext());
    setCanScrollPrev(emblaApi.canScrollPrev());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    updateScrollSnapState(emblaApi);
    emblaApi.on('select', updateScrollSnapState);
    emblaApi.on('reInit', updateScrollSnapState);
  }, [emblaApi, updateScrollSnapState]);

  return (
    <div className="embla__pagination">
      <button
        onClick={() => emblaApi.scrollPrev()}
        aria-label="Scroll to previous slide"
        disabled={!canScrollPrev}
        className="embla__pagination-arrow"
      >
        <SvgIcon iconName="arrow-left-portal" viewBox="0 0 24 24" />
      </button>
      <div className="embla__pagination-numbers">
        <span className="embla__pagination-selected">{selectedSnap + 1}</span>
        <span className="embla__pagination-slash">/</span>
        <span className="embla__pagination-total">{snapCount}</span>
      </div>
      <button
        onClick={() => emblaApi.scrollNext()}
        aria-label="Scroll to next slide"
        disabled={!canScrollNext}
        className="embla__pagination-arrow"
      >
        <SvgIcon iconName="arrow-right-portal" viewBox="0 0 24 24" />
      </button>
    </div>
  );
}
